/*
 * Signing in: a mobile number, then a code.
 *
 * There is no password and no way to register. An admin puts a number on the
 * list and that number can then ask for a code; anything else is told exactly
 * what a valid number is told, because an endpoint that answers "no such
 * account" differently is an endpoint that will happily tell anyone who has
 * one.
 *
 * The code field is `inputMode="numeric"` with `autoComplete="one-time-code"`,
 * which on a phone offers the code from the SMS as soon as it arrives, and on
 * Android fills it automatically — the app hash in the message body is there
 * for exactly that.
 */

import { useEffect, useRef, useState } from "react";

import { useRequestOtp, useVerifyOtp } from "../api/hooks";

export function SignInPage() {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState<"mobile" | "otp">("mobile");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  const request = useRequestOtp();
  const verify = useVerifyOtp();
  const otpField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === "otp") otpField.current?.focus();
  }, [stage]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    request.mutate(mobile, {
      onSuccess: () => {
        setStage("otp");
        setCountdown(60);
      },
      onError: (err) => setError(err instanceof Error ? err.message : "Could not send a code."),
    });
  };

  const check = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    verify.mutate(
      { mobile, otp },
      {
        onError: (err) => {
          setError(err instanceof Error ? err.message : "That code was not right.");
          setOtp("");
          otpField.current?.focus();
        },
      },
    );
  };

  return (
    <div className="signin-page">
      <div className="signin-card">
        <div className="signin-brand">
          <div className="brand-mark">
            <svg width="17" height="17" viewBox="0 0 32 32" fill="none">
              <path
                d="M5 22 L12 15 L17 19 L27 8"
                stroke="#2fd07a"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="27" cy="8" r="2.6" fill="#2fd07a" />
            </svg>
          </div>
          <div className="brand-name">
            HKM Invest
            <small>Algo trading</small>
          </div>
        </div>

        {stage === "mobile" ? (
          <form onSubmit={send}>
            <h1>Sign in</h1>
            <p className="lede">
              We will text a code to your mobile. Only numbers an administrator has
              added can sign in.
            </p>
            <div className="field">
              <label htmlFor="mobile">Mobile number</label>
              <input
                id="mobile"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="10 digits"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                maxLength={14}
                required
                autoFocus
              />
            </div>
            {error && <div className="banner danger mt"><div className="body">{error}</div></div>}
            <button
              className="btn primary block mt"
              type="submit"
              disabled={request.isPending || mobile.replace(/\D/g, "").length < 10}
            >
              {request.isPending ? <span className="spinner" /> : "Send code"}
            </button>
          </form>
        ) : (
          <form onSubmit={check}>
            <h1>Enter the code</h1>
            <p className="lede">
              Sent to {mobile}.{" "}
              <button
                type="button"
                className="btn ghost sm"
                style={{ padding: "2px 8px" }}
                onClick={() => {
                  setStage("mobile");
                  setOtp("");
                  setError(null);
                }}
              >
                Change
              </button>
            </p>
            <div className="field">
              <label htmlFor="otp">6-digit code</label>
              <input
                id="otp"
                ref={otpField}
                className="otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            {error && <div className="banner danger mt"><div className="body">{error}</div></div>}
            <button
              className="btn primary block mt"
              type="submit"
              disabled={verify.isPending || otp.length < 4}
            >
              {verify.isPending ? <span className="spinner" /> : "Sign in"}
            </button>
            <button
              type="button"
              className="btn ghost block mt"
              disabled={countdown > 0 || request.isPending}
              onClick={() => {
                setError(null);
                request.mutate(mobile, {
                  onSuccess: () => setCountdown(60),
                  onError: (err) =>
                    setError(err instanceof Error ? err.message : "Could not send a code."),
                });
              }}
            >
              {countdown > 0 ? `Send again in ${countdown}s` : "Send another code"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
